/**
 * 2048 Statistics Service
 *
 * Records game results via StatisticsManager, tracks best score,
 * highest tile, games played, and win rate.
 *
 * Follows SudokuStatisticsService pattern.
 */

import {
  Game2048Statistics,
  Game2048GameResult,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class Game2048StatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'game_2048_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): Game2048Statistics {
    return {
      gamesPlayed: 0,
      bestScore: 0,
      highestTile: 0,
      totalScore: 0,
      gamesWon: 0,
      winPercentage: 0,
      averageScore: 0,
      totalMoves: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): Game2048Statistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as Game2048Statistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get 2048 statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(result: Game2048GameResult, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);

      stats.gamesPlayed++;
      stats.totalScore += result.score;
      stats.totalMoves += result.moveCount;

      if (result.score > stats.bestScore) {
        stats.bestScore = result.score;
      }

      if (result.highestTile > stats.highestTile) {
        stats.highestTile = result.highestTile;
      }

      if (result.won) {
        stats.gamesWon++;
      }

      stats.winPercentage =
        stats.gamesPlayed > 0
          ? (stats.gamesWon / stats.gamesPlayed) * 100
          : 0;

      stats.averageScore =
        stats.gamesPlayed > 0
          ? Math.round(stats.totalScore / stats.gamesPlayed)
          : 0;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record 2048 game result:', error);
      return false;
    }
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset 2048 statistics:', error);
      return false;
    }
  }
}
